import { formatTimer, PrimaryButton, ReactIcons, Spacer, TopBar, useThemeContext } from "@kalyzee/kalyzee-ui";
import React, { useMemo, useState } from "react";
import { Touchable, UserAvatar, useOuterClickRef } from "@kalyzee/kast-web-sdk/web-components";
import { ParticipantsPopover } from "./ParticipantsPopover";
import { UserCardAvatarArea } from "./UserCardAvatarArea";
import { getNormalUser } from "../../helpers/user";

import styles from "./BaseTopBar.module.css";
import { UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { User } from "@kalyzee/kast-web-sdk/websocket";
import { RecordIndicator } from "../utils/RecordIndicator";
import { MainClockRenderer } from "../renderers/mainClock";
import { RoleBadge } from "./RoleBadge";
import { useClampedDate } from "../../hooks/utils";
import { getEnvClientLogo } from "../../helpers/env";
import { useTopBarSlotTarget } from "./TopBarSlot";

export interface BaseTopBarProps {
  user?: User | UserOutputDTO;
  meetingName?: string;
  meetingStartedAt?: string;
  showDate?: boolean;
  showLogo?: boolean;
  showFullscreenButton?: boolean;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  watchingNumber?: number;
  isSpectator?: boolean;
  isExpert?: boolean;
  settingsBtnRef?: React.Ref<HTMLDivElement>;
  meetingTimerRef?: React.Ref<HTMLDivElement>;
  onBack?: () => void;
  /** Icon rendered inside the back button. Default: the classic back arrow. */
  backIcon?: "arrow" | "exit";
  onSettings?: () => void;
  onMeetingTimerClick?: () => void;
}

const _BaseTopBar: React.FC<BaseTopBarProps> = ({
  user,
  meetingName,
  meetingStartedAt,
  showDate,
  showLogo,
  showFullscreenButton,
  isFullscreen,
  onToggleFullscreen,
  watchingNumber,
  isSpectator,
  isExpert,
  settingsBtnRef,
  meetingTimerRef,
  onBack,
  backIcon,
  onSettings,
  onMeetingTimerClick,
}) => {
  const themeCtx = useThemeContext();

  const meetingStartedAtDate = useClampedDate(meetingStartedAt);

  // Co-branding: a client logo left of ours, from the CLIENT_LOGO(S) env var.
  // Read once — it cannot change without a reload.
  const [clientLogo] = useState(() => getEnvClientLogo());
  const [clientLogoFailed, setClientLogoFailed] = useState(false);
  const showClientLogo = !!clientLogo && !clientLogoFailed;

  const [participantsOpen, setParticipantsOpen] = useState(false);
  const participantsRef = useOuterClickRef(() => setParticipantsOpen(false));

  // Pages render their own controls here (see TopBarSlot); no provider, no slot.
  const setSlotTarget = useTopBarSlotTarget();

  const left = useMemo(() => {
    const items: React.ReactNode[] = [];
    // Optional : Back
    if (onBack) {
      items.push(
        <PrimaryButton
          className={styles.backArrow}
          onClick={onBack}
        >
          {backIcon === "exit" ? <ReactIcons.Exit /> : <ReactIcons.ArrowBack />}
        </PrimaryButton>
       );
    }

    // Optional : Settings
    if (onSettings) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <div ref={settingsBtnRef}>
          <PrimaryButton
            className={styles.settings}
            onClick={onSettings}
          >
            <ReactIcons.Settings />
          </PrimaryButton> 
        </div>
      );
    }

    // Optional : User
    if (user) {
      const { displayName, avatar } = getNormalUser(user);
      if (items.length) items.push(<Spacer />);
      items.push(
        <span className={styles.userAvatar}>
          <UserCardAvatarArea user={{ id: user.id, username: displayName, avatar }}>
            <UserAvatar
              style={{ width: 26, height: 26 }}
              mode="image"
              image={avatar}
              username={displayName}
            />
          </UserCardAvatarArea>
        </span>
      );
      items.push(<Spacer />);
      items.push(<span className={styles.userName}>{displayName}</span>);
      if (isSpectator) {
        items.push(<Spacer />);
        items.push(<RoleBadge variant="spectator" className={styles.roleBadge} />);
      }
      if (isExpert) {
        items.push(<Spacer />);
        items.push(<RoleBadge variant="expert" className={styles.roleBadge} />);
      }
    }

    return items;
  }, [user, isSpectator, isExpert, onBack, backIcon, themeCtx.theme, themeCtx.setPreferredTheme]);

  const right = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (meetingName || meetingStartedAtDate || watchingNumber !== undefined) {
      items.push(
        <div className={styles.meetingData}>
          {meetingName && (
            // Optional : Meeting name
            <div className={styles.meetingName}>
              {meetingName}
            </div>
          )}
          {meetingStartedAtDate && (
            <div ref={meetingTimerRef}>
              <PrimaryButton
                className={styles.meetingMetrics}
                onClick={onMeetingTimerClick}
              >
                <RecordIndicator className={styles.recordIndicator} />
                <div className={styles.elapsedTime}>
                  <MainClockRenderer
                    render={_ => formatTimer(meetingStartedAtDate)}
                  />
                </div>
              </PrimaryButton>
            </div>
          )}
          {watchingNumber !== undefined && (
            <div ref={participantsRef as React.RefObject<HTMLDivElement>} className={styles.watchersAnchor}>
              <Touchable
                className={styles.watchers}
                onPress={() => setParticipantsOpen(open => !open)}
              >
                <ReactIcons.Eye style={{ width: "1em", height: "1em" }} />
                <span>{watchingNumber}</span>
              </Touchable>
              {participantsOpen && <ParticipantsPopover />}
            </div>
          )}
        </div>
      );
    }

    // Optional : Date
    if (showDate) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <MainClockRenderer
          render={date => {
            let dateString = date.toLocaleDateString(undefined, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            });
            dateString = dateString.charAt(0).toUpperCase() + dateString.slice(1);
            let timeString = date.toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });
            return (
              <span className={styles.date}>{dateString} - {timeString}</span>
            );
          }}
        />
      );
    }

    // Optional : Fullscreen toggle (far right)
    if (showFullscreenButton && onToggleFullscreen) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <PrimaryButton
          className={styles.fullscreen}
          onClick={onToggleFullscreen}
          nativeProps={{ title: isFullscreen ? "Quitter le plein écran" : "Plein écran" }}
        >
          {isFullscreen ? <ReactIcons.Shrink /> : <ReactIcons.Expand />}
        </PrimaryButton>
      );
    }

    // Optional : page slot (far right). Always mounted while a provider exists so the portal
    // target is stable; it takes no room while empty.
    if (setSlotTarget) {
      items.push(<div ref={setSlotTarget} className={styles.slot} />);
    }

    return items;
  }, [meetingName, meetingStartedAtDate, showDate, watchingNumber, participantsOpen, onMeetingTimerClick, showFullscreenButton, isFullscreen, onToggleFullscreen, setSlotTarget]);

  const center = useMemo(() => {
    const items: React.ReactNode[] = [];
    // Optional : Logo, preceded by the client's when the deployment sets one
    if (showLogo) {
      items.push(
        <div className={styles.logos}>
          {showClientLogo && (
            <>
              <img
                className={styles.clientLogo}
                src={clientLogo}
                alt=""
                // A logo that fails to load must not leave a broken image nor a
                // dangling separator in the bar.
                onError={() => setClientLogoFailed(true)}
              />
              <svg className={styles.logoSeparator} viewBox="0 0 12 12" aria-hidden focusable="false">
                <path d="M2.6 2.6 L9.4 9.4 M9.4 2.6 L2.6 9.4" />
              </svg>
            </>
          )}
          <ReactIcons.LogoFullAnimated style={{ width: 85, height: 24 }} />
        </div>,
      );
    }
    return items;
  }, [showLogo, showClientLogo, clientLogo]);

  return (
    <TopBar
      left={left}
      right={right}
      center={center}
      className={styles.container}
    />
  );
}

export const BaseTopBar = React.memo(_BaseTopBar);